import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { usePutUsersProfile } from '@/types/origin-point-engine'
import { showSuccessData, showErrorData } from '@/lib/toast'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useUser } from '@/features/auth/hooks/use-user'
import { AvatarUploader } from './avatar-uploader'
import { ChangePasswordForm } from './change-password-form'
import { ChangePhoneForm } from './change-phone-form'

const profileFormSchema = z.object({
  username: z
    .string()
    .min(2, 'Username must be at least 2 characters.')
    .max(30, 'Username must not be longer than 30 characters.'),
  email: z.string().email().optional(), // Make optional since we might just display it
  bio: z.string().max(160).optional(), // Bio is optional
})

type ProfileFormValues = z.infer<typeof profileFormSchema>

export function ProfileForm() {
  const { userProfile, refeshUserProfile } = useUser()

  const { mutateAsync: updateUserProfile } = usePutUsersProfile()

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    mode: 'onChange',
    defaultValues: {
      username: '',
      email: '',
      bio: '',
    },
    values: {
      username: userProfile?.username || '',
      email: userProfile?.email || '',
      bio: userProfile?.bio || '',
    },
  })

  async function onSubmit(data: ProfileFormValues) {
    try {
      await updateUserProfile({
        data: {
          username: data.username,
          bio: data.bio,
        },
      })
      showSuccessData('资料更新成功')
      //更新成功后刷新用户信息
      refeshUserProfile()
    } catch (error) {
      showErrorData('资料更新失败', error)
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-8'>
        <AvatarUploader
          initialUrl={userProfile?.avatarUrl ?? userProfile?.avatar ?? null}
          onUploaded={() => refeshUserProfile()}
        />
        <FormField
          control={form.control}
          name='username'
          render={({ field }) => (
            <FormItem>
              <FormLabel>用户名</FormLabel>
              <FormControl>
                <Input placeholder='您的用户名' {...field} />
              </FormControl>
              <FormDescription>这是您的公开显示名称</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='email'
          render={({ field }) => (
            <FormItem>
              <FormLabel>邮箱</FormLabel>
              <FormControl>
                <Input
                  placeholder='Email'
                  {...field}
                  disabled
                  className='bg-muted'
                />
              </FormControl>
              <FormDescription>无法修改</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='bio'
          render={({ field }) => (
            <FormItem>
              <FormLabel>简介</FormLabel>
              <FormControl>
                <Textarea
                  placeholder='Tell us a little bit about yourself'
                  className='resize-none'
                  {...field}
                />
              </FormControl>
              <FormDescription>介绍介绍你自己吧</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className='flex items-center gap-4'>
          <Button type='submit'>更新资料</Button>
          <ChangePasswordForm />
          <ChangePhoneForm />
        </div>
      </form>
    </Form>
  )
}
